// Audit — the metering ledger (UX5).
//
// Every billable fact the gateway recorded for the caller: which tool, which user,
// GPU-seconds, bytes and tokens, newest first. It was the "usage" tab of Overview's
// Activity block, wedged between a live audit feed and a queue table — three
// different questions, one tab bar. Usage is not "what is happening"; it is "what
// was consumed", which is the one question you come back to a week later, so it
// gets a page whose name says so.
//
// The endpoint (GET /usage) is scoped to the caller server-side: a member's ledger
// is their own, an admin's is the org's. This page never narrows a payload.

import { api } from '../api/client';
import { useResource } from '../lib/useResource';
import { Loading, ErrorState, bytesHuman } from '../components/ui';
import { DataTable, type Column } from '../components/DataTable';
import { fmt } from '../lib/feedMerge';
import type { UsageRecord } from '../api/schemas';

export function Audit() {
  return (
    <div className="view">
      <div className="view-head">
        <h1>Audit</h1>
      </div>
      <UsageLedger />
    </div>
  );
}

function UsageLedger() {
  const res = useResource(() => api.usage(), []);
  const cols: Column<UsageRecord>[] = [
    { key: 'ts', header: 'Time', cell: (u) => <span className="muted">{fmt(u.ts)}</span> },
    { key: 'tool', header: 'Tool', cell: (u) => <code className="mono">{u.tool}</code> },
    { key: 'user', header: 'User', cell: (u) => u.user ?? '—' },
    { key: 'gpu', header: 'GPU·s', align: 'right', cell: (u) => u.gpu_seconds.toFixed(1) },
    { key: 'bytes', header: 'Bytes', align: 'right', cell: (u) => bytesHuman(u.bytes) },
    { key: 'tokens', header: 'Tokens', align: 'right', cell: (u) => u.tokens },
  ];
  if (res.loading) return <Loading what="Loading usage…" />;
  if (res.error || !res.data) return <ErrorState message={res.error ?? 'no data'} onRetry={res.reload} />;

  const rows = res.data.items;
  const totals = rows.reduce(
    (a, u) => ({ gpu: a.gpu + u.gpu_seconds, bytes: a.bytes + u.bytes, tokens: a.tokens + u.tokens }),
    { gpu: 0, bytes: 0, tokens: 0 },
  );

  return (
    <section className="card">
      <header className="card-head"><h2>Usage ledger</h2></header>
      <div className="card-body">
        <div className="muted-row" data-testid="usage-totals">
          <span>Σ {totals.gpu.toFixed(1)} GPU·s</span>
          <span>{bytesHuman(totals.bytes)}</span>
          <span>{totals.tokens} tokens</span>
          <span>{rows.length} records</span>
        </div>
        <DataTable columns={cols} rows={rows} rowKey={(u) => u.call_id} empty="No usage records." />
      </div>
    </section>
  );
}
